import { useCallback, useEffect, useRef, useState } from "react";
import { useImageRatio, usePaintedImageFrame } from "@/hooks/usePaintedImageFrame";
import { HERO_IMAGE_URL } from "@/pages/home/components/heroImageUrl";

const ADMINISTRATOR_CUTOUT =
  "/imagens-administradora/administradora-animacao.png";

const APPEAR_DELAY = 1000;
const VISIBLE_TIME = 5000;
const EXIT_TIME = 500;
export const REPLAY_GREETING_EVENT = "porta31:replay-greeting";

/**
 * Posição da porta de entrada, em frações do próprio ficheiro da imagem de capa
 * (0 = bordo esquerdo/topo, 1 = bordo direito/fundo). Assim a caixa acompanha
 * sempre o edifício, seja qual for o tamanho do ecrã.
 */
const DOOR = {
  left: 0.666,
  top: 0.653,
  width: 0.078,
  height: 0.16,
};

/**
 * Em ecrãs pequenos a fotografia é ajustada inteira, por isso o vão da porta
 * fica com poucos píxeis de largura. Garantimos aqui um alvo de toque confortável
 * (centrado na porta) para que seja fácil de carregar com o dedo.
 */
const COMPACT_MIN_WIDTH = 88;
const COMPACT_MIN_HEIGHT = 118;

/** Aumento global pedido para a animação (administradora + balão). */
const ANIMATION_SCALE = 1.1;

/** Deslocação da animação em relação à porta: 0,5 cm (~18,9px a 96dpi). */
const ANIMATION_SHIFT_PX = 0.5 * 37.795;

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);

    update();
    mql.addEventListener("change", update);

    return () => mql.removeEventListener("change", update);
  }, [query]);

  return matches;
}

function useIsCompact() {
  return useMediaQuery("(max-width: 767px)");
}

function useIsDesktop() {
  return useMediaQuery("(min-width: 1024px)");
}

/** Tamanho de letra dos botões do hero no desktop (text-sm). */
const DESKTOP_BUBBLE_FONT = 14;

export default function HeroGreeting() {
  const containerRef = useRef<HTMLDivElement>(null);
  const compact = useIsCompact();
  const desktop = useIsDesktop();

  /**
   * O contorno da porta é mostrado por estado (e não por :hover do CSS), porque
   * em ecrãs táteis o :hover fica "preso" e o contorno nunca desaparecia.
   */
  const [hintVisible, setHintVisible] = useState(false);
  const hoverLocked = useRef(false);
  const touchHintTimer = useRef<number | null>(null);
  const ratio = useImageRatio(HERO_IMAGE_URL);
  const frame = usePaintedImageFrame(containerRef, ratio, "contain", "top", "cover", "center");

  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);
  const [run, setRun] = useState(0);

  const appearTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);
  const unmountTimer = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (appearTimer.current !== null) {
      window.clearTimeout(appearTimer.current);
      appearTimer.current = null;
    }
    if (hideTimer.current !== null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    if (unmountTimer.current !== null) {
      window.clearTimeout(unmountTimer.current);
      unmountTimer.current = null;
    }
  }, []);

  const play = useCallback(
    (delay: number) => {
      clearTimers();
      appearTimer.current = window.setTimeout(() => {
        appearTimer.current = null;
        setMounted(true);
        setClosing(false);
        setRun((value) => value + 1);

        hideTimer.current = window.setTimeout(() => {
          hideTimer.current = null;
          setClosing(true);
          unmountTimer.current = window.setTimeout(() => {
            unmountTimer.current = null;
            setMounted(false);
            setClosing(false);
          }, EXIT_TIME);
        }, VISIBLE_TIME);
      }, delay);
    },
    [clearTimers],
  );

  useEffect(() => {
    play(APPEAR_DELAY);
    return clearTimers;
  }, [play, clearTimers]);

  // A seta "voltar ao topo" também volta a chamar a administradora.
  useEffect(() => {
    const handler = () => play(420);
    window.addEventListener(REPLAY_GREETING_EVENT, handler);
    return () => window.removeEventListener(REPLAY_GREETING_EVENT, handler);
  }, [play]);

  // Assim que a animação começa, o contorno desaparece (nunca ficam sobrepostos).
  useEffect(() => {
    if (mounted) setHintVisible(false);
  }, [mounted]);

  useEffect(() => () => {
    if (touchHintTimer.current !== null) {
      window.clearTimeout(touchHintTimer.current);
    }
  }, []);

  const doorRect = frame
    ? {
        left: frame.left + DOOR.left * frame.width,
        top: frame.top + DOOR.top * frame.height,
        width: DOOR.width * frame.width,
        height: DOOR.height * frame.height,
      }
    : null;

  /**
   * A animação toda (administradora + balão) recua 0,5 cm. Em ecrãs pequenos o
   * valor é limitado para ela não sair do vão da porta.
   */
  const animationShift = doorRect
    ? Math.min(ANIMATION_SHIFT_PX, doorRect.width * 0.25)
    : 0;

  // Área clicável: no telemóvel nunca fica menor do que um retângulo confortável.
  const hitRect = doorRect
    ? (() => {
        if (!compact) return doorRect;

        const width = Math.max(doorRect.width, COMPACT_MIN_WIDTH);
        const height = Math.max(doorRect.height, COMPACT_MIN_HEIGHT);

        return {
          left: doorRect.left + (doorRect.width - width) / 2,
          top: doorRect.top + (doorRect.height - height) / 2,
          width,
          height,
        };
      })()
    : null;

  const personHeight = doorRect ? doorRect.height * 0.95 * ANIMATION_SCALE : 0;
  const bubbleFont = desktop
    ? DESKTOP_BUBBLE_FONT
    : Math.min(22, Math.max(11, personHeight * 0.13));
  const bubblePaddingY = Math.max(5, personHeight * 0.055);
  const bubblePaddingX = Math.max(9, personHeight * 0.1);

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0 z-20">
      {doorRect && hitRect && (
        <>
          {/* Zona clicável fixa sobre a porta do edifício: repete as boas-vindas */}
          <button
            type="button"
            onClick={() => {
              // O contorno desaparece no clique e só volta quando o rato sair e entrar de novo.
              hoverLocked.current = true;
              setHintVisible(false);
              play(0);
            }}
            onPointerEnter={(event) => {
              if (event.pointerType === "touch") return;
              if (hoverLocked.current || mounted) return;
              setHintVisible(true);
            }}
            onPointerLeave={() => {
              hoverLocked.current = false;
              setHintVisible(false);
            }}
            onPointerDown={(event) => {
              if (event.pointerType !== "touch") return;
              setHintVisible(true);
              if (touchHintTimer.current !== null) {
                window.clearTimeout(touchHintTimer.current);
              }
              touchHintTimer.current = window.setTimeout(() => {
                touchHintTimer.current = null;
                setHintVisible(false);
              }, 160);
            }}
            aria-label="Porta de entrada do edifício — ver a mensagem de boas-vindas"
            title="Carregue na porta para rever as boas-vindas"
            style={{
              left: hitRect.left,
              top: hitRect.top,
              width: hitRect.width,
              height: hitRect.height,
              minWidth: compact ? COMPACT_MIN_WIDTH : undefined,
              minHeight: compact ? COMPACT_MIN_HEIGHT : undefined,
            }}
            className="group pointer-events-auto absolute block cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background-50"
          >
            {/* Contorno sempre preenchido: um retângulo do tamanho exato do alvo
                de toque/click, em vez de colapsar numa linha. */}
            <span
              style={
                compact
                  ? { top: 0, right: 0, bottom: 0, left: 0 }
                  : { top: 3.2, right: 5.6, bottom: -3.2, left: -5.6 }
              }
              className={`pointer-events-none absolute block rounded-md border transition-all duration-300 group-focus-visible:border-background-50/70 ${
                hintVisible && !mounted
                  ? "border-background-50/55 bg-background-50/10"
                  : "border-background-50/0 bg-background-50/0"
              }`}
              aria-hidden="true"
            />
            <span
              className={`absolute left-1/2 top-full mt-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-foreground-950/75 px-2 py-1 text-[10px] font-medium text-background-50 transition-opacity duration-300 md:block ${
                hintVisible && !mounted ? "opacity-100" : "opacity-0"
              }`}
            >
              Carregue na porta
            </span>
          </button>

          {/* A administradora, sempre dentro do vão da porta */}
          <div
            aria-hidden={!mounted}
            className="absolute z-[7]"
            style={{
              left: doorRect.left + doorRect.width / 2 - animationShift,
              top: doorRect.top + doorRect.height,
              height: personHeight,
              transform: "translate(-50%, -100%)",
            }}
          >
            {mounted && (
              <div key={run} className={`relative h-full ${closing ? "greet-hide" : "greet-show"}`}>
                <div
                  className="greet-bubble absolute bottom-full left-1/2 rounded-lg border border-background-50/35 bg-background-50/10 backdrop-blur-md"
                  style={{
                    marginBottom: Math.max(6, personHeight * 0.08),
                    padding: `${bubblePaddingY}px ${bubblePaddingX}px`,
                    transform: "translateX(-50%)",
                  }}
                >
                  <p
                    className="whitespace-nowrap font-body font-semibold leading-tight text-background-50 drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)]"
                    style={{ fontSize: bubbleFont }}
                  >
                    Bem vindos ao Porta 31
                  </p>
                </div>

                <div
                  className="absolute bottom-0 left-1/2 w-[80%] -translate-x-1/2 rounded-[50%] bg-foreground-950/30 blur-[2px]"
                  style={{ height: Math.max(3, personHeight * 0.045) }}
                  aria-hidden="true"
                />

                <img
                  src={ADMINISTRATOR_CUTOUT}
                  alt="Administradora da Porta31 à entrada do edifício"
                  className="greet-rise relative block h-full w-auto max-w-none drop-shadow-[0_6px_10px_rgba(0,0,0,0.35)]"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}